/**
 * WHEN TO ASK someone to have their agent read the messaging guide.
 *
 *   owner, 2026-08-29: 新建 Agent 以及 Fork Agent 在使用者進入 Agent session 的畫面就
 *   直接彈出來 README 的提示. 並且讓使用者選擇是否讓 Agent 來閱讀, 或是 ignore.
 *   […] 只要 Agent 的 readme hash 落後, 那麼操作如 1.
 *
 * Two rules that pull against each other, and the whole of this file is the seam:
 * ASK ON ARRIVAL (so a fresh or forked agent is offered the guide before anyone tries
 * to use it), and LET IT BE IGNORED (so the offer is not a toll gate on every visit).
 * A dismissal that is remembered forever breaks the second rule's own condition —
 * the guide CHANGES, and the owner's rule for that is explicit: hash 落後就再提示.
 *
 * So an ignore names the guide it dismissed. Ignoring silences that guide for that
 * agent; a new guide is new information and asks again. That is also why the edge
 * sends `guide_hash` at all — see agentRoutes' roster row.
 *
 * Nothing here decides whether the agent has read anything: `read_guide === false`
 * already folds "never read" and "read an older one" into the single answer the UI
 * acts on. This only answers "have we already been told to stop asking about THIS".
 *
 * NEITHER PROMPT FOR AN EDGE THAT BRINGS THE GUIDE ITSELF — owner, 2026-09-29:
 *
 *   情境1: 有一個 Agent 之前從來沒有使用過 avengents 的溝通系統, 那麼在他第一次被使用者
 *   觸發 <ping-agent ... > 的時候, 可以在該信息後面添加讓他來讀 avengents readme 的信息
 *   […] 這樣以後開啟新的 Agent 就再也不需要去讓他們預先讀取 avengents readme 這樣的信息了.
 *
 * An edge that does that says `guide_on_ping` on its rows: the owner's first `@` to an
 * unread agent carries the readme line (the edge's firstContact.ts), and every CLI on
 * that machine has the `avengents` skill (its agentSkill.ts). Both prompts are kept
 * for an edge too old to say so — there a `@` would reach an agent with no way to act
 * on it, which is what they were built to prevent.
 */

/** Where the dismissals live. One key, one small object, per browser. */
const KEY = 'av_guide_ignored'

/**
 * "Automatically approve next time" — owner, 2026-09-21: 我們每次在想要讓 Agent 來讀取
 * 新的 README 的時候呢,都需要 Approve 這個動作。有點麻煩…加上一個 Checkbox…這樣子的話
 * 就不需要讓使用者每次都手動去按這個按鍵了.
 *
 * WHY THIS IS ONE FLAG AND NOT A PER-AGENT ONE. The dismissals above are per (agent,
 * guide) because ignoring is a judgement about one agent. This is the opposite kind of
 * answer: he is not saying anything about an agent, he is saying he does not want to be
 * asked the question. A per-agent version would still meet him once per agent, and he
 * runs eleven — which is the complaint.
 *
 * PER BROWSER, like the dismissals beside it, rather than on the account. It decides
 * whether a DIALOG opens, not what the system does — a phone and a desktop may
 * reasonably differ, and putting it on the account would buy cross-device consistency
 * for a prompt at the cost of a route, a migration and a deploy.
 *
 * IT DOES NOT AUTO-APPROVE ANYTHING ELSE. The only action it takes is the one this
 * dialog's button takes: ask the agent to run `avengents readme`. If the agent's own
 * CLI stops for permission, that prompt still waits in its terminal — this cannot and
 * must not answer that.
 */
const AUTO_KEY = 'av_guide_auto_approve'

export function guideAutoApprove(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) === '1'
  } catch {
    // Blocked or full storage means "not set", which is the safe answer: the person
    // is asked, exactly as before. A read that throws must never approve on its own.
    return false
  }
}

export function setGuideAutoApprove(on: boolean): void {
  try {
    if (on) localStorage.setItem(AUTO_KEY, '1')
    else localStorage.removeItem(AUTO_KEY)
  } catch {
    // Costs the checkbox its memory, never a broken screen — same trade as above.
  }
}

/**
 * An agent is named by (session, host) — the same pair the roster and the URL use.
 *
 * NOT by `hash`: a rename reissues the hash (guideRead.ts's own note), and an ignore
 * that evaporated on rename would re-prompt for an agent the person had just dismissed.
 * The pair is what a person means by "this agent".
 */
function agentKey(session: string, host: string | null | undefined): string {
  // Length-prefixed rather than joined by a delimiter: a host may contain anything a
  // hostname may, so any separator I picked could also appear inside the host and let
  // two different agents share one key. The prefix makes the split unambiguous, and
  // every byte of it stays printable (this repo refuses raw control bytes in source,
  // and the first version of this line used a NUL - the guard caught it).
  const h = host ?? ''
  return `${h.length}:${h}:${session}`
}

type Ignored = Record<string, string>

function load(): Ignored {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(KEY) ?? '{}')
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
    const out: Ignored = {}
    // Keep only string→string: a hand-edited or half-written value must not make
    // every later read throw, which would silently turn every ignore back on.
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Has this person already said "not now" about THIS guide for THIS agent?
 *
 * An absent `guideHash` answers NO. That is the deliberate direction: an edge too old
 * to send one cannot have its dismissals scoped, and silently treating "unknown guide"
 * as "the one they dismissed" would let one ignore swallow every future guide. Asking
 * once too often is recoverable; never asking again is not.
 */
export function guideIgnored(
  session: string,
  host: string | null | undefined,
  guideHash: string | null | undefined,
): boolean {
  if (!session || !guideHash) return false
  return load()[agentKey(session, host)] === guideHash
}

/** Remember that this guide was dismissed for this agent. */
export function ignoreGuide(
  session: string,
  host: string | null | undefined,
  guideHash: string | null | undefined,
): void {
  if (!session || !guideHash) return
  const all = load()
  all[agentKey(session, host)] = guideHash
  try {
    localStorage.setItem(KEY, JSON.stringify(all))
  } catch {
    // A full or blocked store costs one repeated prompt, never a broken screen.
  }
}

/**
 * How long the machine's own ask is trusted to be doing the asking. A fork boots in
 * seconds and reads the guide in its first minute when it is going to at all; ten
 * minutes covers a slow machine and a queued delivery, and is short enough that a
 * fork that ignored its boot line is offered the guide on the next arrival.
 */
const GUIDE_ASK_GRACE_S = 10 * 60

/**
 * Should the arrival prompt open for the agent just opened?
 *
 * Pure, and the ONLY place the rule lives, so the effect that opens the sheet cannot
 * drift from the tests. `read_guide !== false` covers both "has read the current
 * guide" and an edge too old to send the field — absent must not mean "has not read",
 * for the same reason the `@` gate has always compared against `false` explicitly.
 */
export function shouldPromptGuide(
  agent: {
    session: string
    host: string | null
    read_guide?: boolean
    guide_hash?: string
    guide_asked_at?: number
    guide_on_ping?: boolean
  } | null,
  nowS: number = Date.now() / 1000,
): boolean {
  if (!agent || agent.read_guide !== false) return false
  // The edge brings the guide on the first `@` and in the skill — see the file header.
  if (agent.guide_on_ping === true) return false
  /*
   * THE MACHINE ALREADY ASKED — owner, 2026-09-13: a fork's first instruction is
   * "please run `avengents readme …`", delivered by the edge itself; this prompt then
   * asked the person to send the very same thing while the agent was still booting —
   * 同樣的動作跑出來兩次, 浪費 token 又造成使用者困擾. The edge says when it asked
   * (`guide_asked_at`); while that ask is fresh, hold. An ask the agent has not acted on
   * after the grace is a different fact — then the person is asked as before, and the
   * `@` gate never held in the first place: sending TO an unread agent still needs it.
   */
  if (typeof agent.guide_asked_at === 'number' && nowS - agent.guide_asked_at < GUIDE_ASK_GRACE_S) {
    return false
  }
  return !guideIgnored(agent.session, agent.host, agent.guide_hash)
}

/**
 * Does typing `@` to this agent open the guide sheet instead of the picker?
 *
 * The SENDER's row: `@` composes something this agent will have to act on. Compared
 * against `false` explicitly — an older edge omits `read_guide`, and absent must not
 * make the picker unusable. Never for an edge that says `guide_on_ping`: there the
 * `@` is itself how the guide reaches the agent, so gating it would hold back the
 * very message that fixes what the gate is waiting for.
 */
export function mentionNeedsGuide(
  row: { read_guide?: boolean; guide_on_ping?: boolean } | null | undefined,
): boolean {
  return !!row && row.read_guide === false && row.guide_on_ping !== true
}
